
import { useCallback, useState } from 'react';
import { useTextGeneration } from '../useTextGeneration';
import { 
  HeaderGenerationRequest, 
  HeaderGenerationResult 
} from './types';

export const useHeaderGenerator = () => {
  const { generateText } = useTextGeneration();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorState, setErrorState] = useState<Record<string, string>>({});
  const [retryCount, setRetryCount] = useState<Record<string, number>>({});

  const generateHeader = useCallback(async (
    request: HeaderGenerationRequest
  ): Promise<HeaderGenerationResult> => {
    const requestId = `header-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    console.log('[HeaderGenerator] ===== HEADER GENERATION STARTING =====');
    console.log('[HeaderGenerator] Request:', {
      requestId,
      contentLength: request.content?.length || 0,
      hasOriginalPrompt: !!request.originalPrompt,
      hasContext: !!request.context,
    });

    // Check if we can retry (max 3 attempts)
    const currentRetries = retryCount[requestId] || 0;
    if (currentRetries >= 3) {
      console.log('[HeaderGenerator] Max retries exceeded for request:', requestId);
      return {
        header: request.originalPrompt?.substring(0, 100) || 'Research Header',
        success: false,
        error: 'Maximum retry attempts exceeded',
      };
    }

    setIsProcessing(true);

    try {
      // Clear any previous errors for this request
      setErrorState(prev => {
        const { [requestId]: _, ...rest } = prev;
        return rest;
      });

      // Send just the content to the backend - let the system prompt handle the instructions
      const message = request.content.substring(0, 2000);

      console.log('[HeaderGenerator] Calling text generation with content length:', message.length);

      const generatedText = await generateText({
        message,
        type: 'header_generation',
        appName: 'Research Library',
        functionName: 'Research Header Generation'
      });

      if (!generatedText) {
        throw new Error('No header generated from text generation service');
      }

      // Clean up the generated header
      const cleanHeader = generatedText
        .trim()
        .replace(/^["']|["']$/g, '') // Remove quotes
        .replace(/\n.*$/s, '') // Take only first line
        .substring(0, 100); // Ensure max length

      console.log('[HeaderGenerator] ===== HEADER GENERATION SUCCESSFUL =====');
      console.log('[HeaderGenerator] Generated header:', cleanHeader);

      return {
        header: cleanHeader,
        success: true,
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during header generation';
      
      console.error('[HeaderGenerator] ===== HEADER GENERATION FAILED =====');
      console.error('[HeaderGenerator] Error:', errorMessage);

      // Record failure
      setErrorState(prev => ({ ...prev, [requestId]: errorMessage }));
      setRetryCount(prev => ({ ...prev, [requestId]: currentRetries + 1 }));

      // Return fallback header
      const fallbackHeader = request.originalPrompt?.substring(0, 100) || 'Research Header';
      
      return {
        header: fallbackHeader,
        success: false,
        error: errorMessage,
      };

    } finally {
      setIsProcessing(false);
      console.log('[HeaderGenerator] ===== HEADER GENERATION COMPLETED =====');
    }
  }, [generateText]);

  const canRetry = useCallback((requestId?: string): boolean => {
    if (!requestId) return true;
    const attempts = retryCount[requestId] || 0;
    return attempts < 3;
  }, [retryCount]);

  const hasError = useCallback((requestId?: string): boolean => {
    if (!requestId) return Object.keys(errorState).length > 0;
    return !!errorState[requestId];
  }, [errorState]);

  const clearError = useCallback((requestId: string) => {
    setErrorState(prev => {
      const { [requestId]: _, ...rest } = prev;
      return rest;
    });
    setRetryCount(prev => {
      const { [requestId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  return {
    generateHeader,
    isProcessing,
    hasError,
    canRetry,
    clearError,
    errorState,
  };
};
